import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import { Search, Plus, Shield, Users, UserCheck, UserX } from 'lucide-react';
import UserTable from './components/UserTable';
import AddUserModal from './components/AddUserModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import EditUserModal from './components/EditUserModal';
import { userManagementService } from '../../services/userManagementService';
import UserTroubleshooting from './components/UserTroubleshooting';

const UserManagementSettings = () => {
  const { user, userProfile, loading: authLoading, isAdmin } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    admins: 0
  });

  // Load users on component mount
  useEffect(() => {
    if (user && isAdmin?.()) {
      loadUsers();
    } else if (!authLoading && user && !isAdmin?.()) {
      setError('Admin access required to view user management.');
      setLoading(false);
    } else if (!authLoading && !user) {
      setError('Please sign in to access user management.');
      setLoading(false);
    }
  }, [user, isAdmin, authLoading]);

  // Filter users when search or filters change
  useEffect(() => {
    filterUsers();
    calculateStats();
  }, [users, searchQuery, statusFilter, roleFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userManagementService?.getAllUsers();
      setUsers(data || []);
      setSuccess('Users loaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase()?.trim();
      filtered = filtered?.filter(user => 
        user?.full_name?.toLowerCase()?.includes(query) ||
        user?.email?.toLowerCase()?.includes(query) ||
        user?.role?.toLowerCase()?.includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(user => user?.status === statusFilter);
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered?.filter(user => user?.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const calculateStats = () => {
    const total = users?.length || 0;
    const active = users?.filter(user => user?.status === 'active')?.length || 0;
    const inactive = users?.filter(user => user?.status === 'inactive')?.length || 0;
    const admins = users?.filter(user => user?.role === 'admin')?.length || 0;
    
    setStats({ total, active, inactive, admins });
  };

  const handleAddUser = async (userData) => {
    try {
      setError('');
      const newUser = await userManagementService?.createUser(userData);
      if (newUser) {
        setUsers(prev => [...prev, newUser]);
        setShowAddModal(false);
        setSuccess('User created successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(`Failed to create user: ${error?.message}`);
    }
  };

  const handleEditUser = async (userData) => {
    try {
      setError('');
      const updatedUser = await userManagementService?.updateUser(selectedUser?.id, userData);
      if (updatedUser) {
        setUsers(prev => prev?.map(user => 
          user?.id === selectedUser?.id ? updatedUser : user
        ));
        setShowEditModal(false);
        setSelectedUser(null);
        setSuccess('User updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError(`Failed to update user: ${error?.message}`);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setError('');
      const success = await userManagementService?.deleteUser(selectedUser?.id);
      if (success) {
        setUsers(prev => prev?.filter(user => user?.id !== selectedUser?.id));
        setShowDeleteModal(false);
        setSelectedUser(null);
        setSuccess('User deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(`Failed to delete user: ${error?.message}`);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      setError('');
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const updatedUser = await userManagementService?.updateUserStatus(userId, newStatus);
      if (updatedUser) {
        setUsers(prev => prev?.map(user => 
          user?.id === userId ? { ...user, status: newStatus } : user
        ));
        setSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setError(`Failed to update user status: ${error?.message}`);
    }
  };

  // Check if current user is admin - enhanced check
  const userIsAdmin = isAdmin?.() || user?.role === 'admin' || userProfile?.role === 'admin';

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <Header />
          <main className="p-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5]?.map((i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <Header />
          <main className="p-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-12">
                  <Shield className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
                  <p className="text-gray-600 mb-4">Please sign in to access user management.</p>
                  <button
                    onClick={() => window.location.href = '/login'}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!userIsAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <Header />
          <main className="p-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-12">
                  <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Admin Access Required</h2>
                  <p className="text-gray-600 mb-4">You don't have permission to access user management.</p>
                  <p className="text-sm text-gray-500">Contact your administrator to get admin access.</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <Header />
        
        <main className="p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                  <p className="mt-1 text-gray-600">Manage users, roles, and permissions</p>
                </div>
              </div>

              {/* Current Admin Info */}
              <div className="flex items-center space-x-2 text-sm text-gray-600 bg-green-50 px-3 py-2 rounded-lg">
                <Shield className="h-4 w-4 text-green-600" />
                <span>Signed in as Admin: {user?.email || userProfile?.email || 'Admin User'}</span>
                <div className="w-2 h-2 bg-green-500 rounded-full" title="Admin Access"></div>
              </div>
            </div>

            {/* Success Alert */}
            {success && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                <span className="block sm:inline">{success}</span>
                <button
                  className="absolute top-0 bottom-0 right-0 px-4 py-3"
                  onClick={() => setSuccess('')}
                >
                  <span className="sr-only">Dismiss</span>
                  ×
                </button>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                <span className="block sm:inline">{error}</span>
                <button
                  className="absolute top-0 bottom-0 right-0 px-4 py-3"
                  onClick={() => setError('')}
                >
                  <span className="sr-only">Dismiss</span>
                  ×
                </button>
              </div>
            )}

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats?.total}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Active Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats?.active}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Inactive Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats?.inactive}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Shield className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Administrators</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats?.admins}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-lg shadow">
              {/* Header Actions */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Search Bar */}
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users by name, email, or role..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                    />
                  </div>

                  {/* Filters and Add Button */}
                  <div className="flex items-center gap-4">
                    {/* Status Filter */}
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e?.target?.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>

                    {/* Role Filter */}
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e?.target?.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>

                    {/* Add User Button */}
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add User
                    </button>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="mt-4 text-sm text-gray-600">
                  Showing {filteredUsers?.length} of {users?.length} users
                </div>
              </div>

              {/* User Table */}
              <UserTable
                users={filteredUsers}
                onEdit={(user) => {
                  setSelectedUser(user);
                  setShowEditModal(true);
                }}
                onDelete={(user) => {
                  setSelectedUser(user);
                  setShowDeleteModal(true);
                }}
                onStatusToggle={handleStatusToggle}
              />
            </div>

            {/* Add Troubleshooting Section */}
            <UserTroubleshooting />

            {/* Modals */}
            <AddUserModal
              isOpen={showAddModal}
              onClose={() => setShowAddModal(false)}
              onSubmit={handleAddUser}
            />

            <EditUserModal
              isOpen={showEditModal}
              onClose={() => {
                setShowEditModal(false);
                setSelectedUser(null);
              }}
              onSubmit={handleEditUser}
              user={selectedUser}
            />

            <DeleteConfirmationModal
              isOpen={showDeleteModal}
              onClose={() => {
                setShowDeleteModal(false);
                setSelectedUser(null);
              }}
              onConfirm={handleDeleteUser}
              user={selectedUser}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserManagementSettings;